import { FC, useEffect } from "react";
import { Controller } from "react-hook-form";
import { useI18n } from "../../hooks/useTranslations";
import { Packaging } from "../../http/packaging/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Input from "../ui/Input";
import Numeric from "../ui/Numeric";

export interface PackagingFormBody {
  code: string;
  description: string;
  cost: number;
}

type PackagingFormProps = {
  packaging?: Packaging;
  onSubmit: (data: PackagingFormBody) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const PackagingForm: FC<PackagingFormProps> = ({ packaging, onSubmit, isDisabled, isLoading }) => {
  const i18n = useI18n();

  const defaultValues = {
    code: packaging?.code ?? "",
    description: packaging?.description ?? "",
    cost: packaging?.cost ?? 0,
  };

  return (
    <Form<PackagingFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ setValue, control, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [packaging]);

        return (
          <>
            <Controller
              name="code"
              control={control}
              rules={{ required: i18n.t.common.fieldCannotBeEmpty }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.code} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} {...field} isInvalid={isInvalid} disabled={isDisabled || isLoading} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="description"
              control={control}
              rules={{ required: i18n.t.common.fieldCannotBeEmpty }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.description} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} {...field} isInvalid={isInvalid} disabled={isDisabled || isLoading} />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="cost"
              control={control}
              rules={{
                required: true,
                min: {
                  value: 0.0001,
                  message: i18n.t.common.fieldCannotBeEmpty,
                },
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.cost} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      min={0.0001}
                      decimalScale={4}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading}
                      suffix=" €"
                      onBlur={(value) => setValue("cost", value ?? 0)}
                    />
                  )}
                </FormField>
              )}
            />
          </>
        );
      }}
    </Form>
  );
};

export default PackagingForm;
